import {ChangeDetectionStrategy, Component, EventEmitter, HostListener, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {EditItemComponent} from "../edit-item/edit-item.component";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {AuthService} from "../../_services/auth.service";
import {ContentService} from "../../_services/content.service";
import {SnackbarService} from "../../_services/snackbar.service";
import {interval, Subscription} from "rxjs";
import {KeyboardShortcutsComponent} from "./keyboard-shortcuts/keyboard-shortcuts.component";
import {ConfirmDialogComponent} from "../confirm-dialog/confirm-dialog.component";
import {ConfirmationDialogData} from "../../_types/shared";
import {MoveMediaComponent} from "../../admin/flagged-media/move-media/move-media.component";
import {FullScreenImageComponent} from "../full-screen-image/full-screen-image.component";
import {User} from "../../_types/user";
import {Comment, MediaItem} from "../../_types/memorybox";
import {PostComment} from "../comments/comments.component";

export interface MediaItemWithComments extends MediaItem {
    comments: Comment[];
}

interface MediaData {
    media: MediaItemWithComments[],
    index: number,
    permission?: string,
    timeline: number,
    canFavourite: boolean,
}

@Component({
    selector: 'app-lightbox',
    templateUrl: './lightbox.component.html',
    styleUrl: './lightbox.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class LightboxComponent implements OnDestroy {
    media: MediaItemWithComments[] = [];
    categories: string[] = [];
    index: number = 0;
    currentMedia: MediaItemWithComments | undefined;
    loading: boolean = false;
    allowsComments: boolean = false;
    flagging: boolean = false;
    flagForm: FormGroup;
    permission: string = "View Only";
    user!: User;
    commenting: boolean = false
    commentsLoading: boolean = true;
    canFavourite: boolean = false;
    timeline: number | undefined;
    refresh: EventEmitter<number | null> = new EventEmitter();
    reFetch: EventEmitter<null> = new EventEmitter();
    slideshowInProgress: boolean = false;
    isRaa: boolean = false;
    isMobile: boolean = true;
    private slideshow?: Subscription;

    constructor(@Inject(MAT_DIALOG_DATA) private data: MediaData,
                public dialog: MatDialog,
                public contentService: ContentService,
                protected dialogRef: MatDialogRef<LightboxComponent>,
                private formBuilder: FormBuilder,
                private snackbarService: SnackbarService,
                private authService: AuthService) {
        this.authService.getDynamicSettings().then((r): void => {
            this.allowsComments = r.allow_comments;
            this.isRaa = r.is_raa;
        });
        if (data && data.media) {
            this.media = data.media;
            this.index = data.index;
            this.canFavourite = data.canFavourite;
            this.currentMedia = this.media[this.index];
            this.timeline = data.timeline;
            if (data.permission) {
                this.permission = data.permission
            }
        }
        this.flagForm = this.formBuilder.group({
            title: [''],
            description: [''],
            category: ['', Validators.required],
        });
        this.authService.getUser().subscribe({
            next: r => {
                this.user = r;
            }
        })
        const toMatch: RegExp[] = [
            /Android/i,
            /iPhone/i,
            /iPad/i,
        ];

        this.isMobile = toMatch.some((toMatchItem): RegExpMatchArray | null => {
            return navigator.userAgent.match(toMatchItem);
        });
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent): void {
        if (!this.commenting && !this.flagging) {
            // Don't detect key press if in forms
            if (event.ctrlKey && event.shiftKey && event.keyCode == 32) {
                // space clicked toggle slideshow
                this.toggleSlideshow();
            } else if ((event.ctrlKey && event.shiftKey && event.keyCode == 39) || (event.ctrlKey && event.shiftKey && event.keyCode == 68)) {
                // right arrow or d go forward
                this.move()
            } else if ((event.ctrlKey && event.shiftKey && event.keyCode == 37) || (event.ctrlKey && event.shiftKey && event.keyCode == 65)) {
                // left arrow or a go back
                this.move(true)
            }

        }
    }

    openKeyboardShortcuts(): void {
        this.dialog.open(KeyboardShortcutsComponent);
    }

    ngOnDestroy(): void {
        this.stopSlideshow();
    }

    get canEdit(): boolean {
        return !!this.currentMedia && !!this.user &&
            (this.currentMedia.user?.id === this.user.id || !this.user.role.allow_editing_of_own_content_only);
    }

    move(back?: boolean): void {
        this.currentMedia = undefined;
        if (!back) {
            if (this.index == (this.media.length - 1)) {
                this.index = 0;
            } else {
                this.index++;
            }

        } else {
            if (this.index == 0) {
                this.index = (this.media.length - 1);
            } else {
                this.index--;
            }
        }
        this.currentMedia = this.media[this.index];
        this.commenting = false;
        this.flagging = false;
    }

    toggleFlagMedia(): void {
        if (this.categories.length == 0) {
            this.contentService.feedbackCategories().subscribe({
                next: r => {
                    this.categories = r;
                }, error: error => {
                    console.error(error)
                    this.categories = ["Duplicate", "Move to another album", 'Inappropriate', 'Other']
                }
            });
        }
        this.commenting = false;
        this.flagging = !this.flagging
    }

    flagMedia(): void {
        let body: {
            title: string | null,
            text: string | null,
            category: string,
            flagged_content_index: number | null,
            media_id: number | null,
            timeline_id: number | undefined
        } = {
            title: this.flagForm.value.title,
            text: this.flagForm.value.description,
            category: this.flagForm.value.category,
            flagged_content_index: this.index,
            media_id: this.media[this.index].id,
            timeline_id: this.timeline
        }
        this.contentService.feedback(body).subscribe({
            next: r => {
                this.flagging = !this.flagging;
                this.snackbarService.openSnackBar('Item has been successfully flagged for investigation', 'success')
                // Every successful report hides the item, regardless of category.
                this.refresh.emit(null);
                this.flagForm.reset();
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    deleteMedia(): void {
        if (this.user.role.can_permanently_delete) {
            const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open<ConfirmDialogComponent, ConfirmationDialogData>(ConfirmDialogComponent, {
                panelClass: 'mediumWindow',
                data: {
                    showSubmitBtn: true,
                    showCancelBtn: true,
                    message: 'Are you sure you want to delete this media?',
                    title: 'Delete Media',
                    showHeader: true
                }
            });
            dialogRef.afterClosed().subscribe((resp): void => {
                if (resp) {
                    this.contentService.deleteMedia(this.media[this.index].id).subscribe({
                        next: r => {
                            this.snackbarService.openSnackBar('Media Deleted', 'success')
                            this.refresh.emit(this.index);
                        }, error: error => {
                            this.snackbarService.openSnackBar(error, 'error')
                            console.error(error)
                        }
                    });
                }
            });
        }
    }

    hideMedia(): void {
        if (this.user.role.can_hide_media) {
            let message: string = 'Are you sure you want to delete this media?'
            let title: string = 'Delete Media'
            let success: string = 'Media Deleted'
            if (this.user.role.can_permanently_delete) {
                message = 'Are you sure you want to hide this media?'
                title = 'Hide media'
                success = 'Media Hidden'
            }
            const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open<ConfirmDialogComponent, ConfirmationDialogData>(ConfirmDialogComponent, {
                panelClass: 'mediumWindow',
                data: {
                    showSubmitBtn: true,
                    showCancelBtn: true,
                    message,
                    title,
                    showHeader: true
                }
            });
            dialogRef.afterClosed().subscribe((resp): void => {
                if (resp) {
                    this.contentService.hideMedia(this.media[this.index].id).subscribe({
                        next: r => {
                            this.snackbarService.openSnackBar(success, 'success')
                            this.refresh.emit(this.index);
                        }, error: error => {
                            this.snackbarService.openSnackBar(error, 'error')
                            console.error(error)
                        }
                    });
                }
            });
        }
    }


    unHideMedia(): void {
        if (this.user.role.can_hide_media) {
            this.contentService.unHideMedia(this.media[this.index].id).subscribe({
                next: r => {
                    this.snackbarService.openSnackBar('Media Restored', 'success')
                    this.refresh.emit(this.index);
                }, error: error => {
                    this.snackbarService.openSnackBar(error, 'error')
                    console.error(error)
                }
            });
        }
    }


    toggleCommenting(): void {
        this.flagging = false;
        this.commenting = !this.commenting
        this.commentsLoading = true;
        this.contentService.getMediaComments(this.media[this.index].id).subscribe({
            next: r => {
                this.media[this.index].comments = r;
                this.commentsLoading = false;
                setTimeout((): void => {
                    const element: HTMLElement | null = document.getElementById('chatContent');
                    if (element) {
                        element.scrollTop = element.scrollHeight;
                    }
                }, 50);
            }, error: error => {
                this.commentsLoading = false;
                console.error(error)
            }
        });
    }

    editItem(): void {
        this.dialogRef.close()
        const dialogRef: MatDialogRef<EditItemComponent> = this.dialog.open(EditItemComponent, {
            data: this.currentMedia,
            panelClass: 'largeWindow',
        });
    }

    favouriteMedia(): void {
        if (this.media[this.index].is_favourite) {
            this.contentService.unFavouriteMedia(this.media[this.index].id).subscribe({
                next: r => {
                    this.media[this.index].is_favourite = false;
                    this.snackbarService.openSnackBar('Removed from favourites', 'success')
                    this.reFetch.emit();
                }, error: error => {
                    console.error(error)
                }
            });
        } else {
            this.contentService.favouriteMedia(this.media[this.index].id).subscribe({
                next: r => {
                    this.media[this.index].is_favourite = true;
                    this.snackbarService.openSnackBar('Added to favourites', 'success')
                }, error: error => {
                    console.error(error)
                }
            });

        }
    }

    sendComment(event: PostComment): void {
        this.contentService.createMediaComment(this.media[this.index].id, event.message).subscribe({
            next: r => {
                this.media[this.index].comments = r;
                this.commentsLoading = false;
                this.media[this.index].comment_count++
                setTimeout((): void => {
                    const element: HTMLElement | null = document.getElementById('chatContent');
                    if (element) {
                        element.scrollTop = element.scrollHeight;
                    }
                }, 50);
            }, error: error => {
                this.commentsLoading = false;
                console.error(error)
            }
        });

    }

    download(): void {
        let url: string = '';
        if (!this.currentMedia) return;
        if (this.currentMedia.image) {
            url = this.currentMedia.image
        } else if (this.currentMedia.video) {
            url = this.currentMedia.video
        } else if (this.currentMedia.audio) {
            url = this.currentMedia.audio
        } else if (this.currentMedia.document) {
            url = this.currentMedia.document
        }
        this.contentService.downloadMedia(this.currentMedia, url).catch(e => {
            console.error(e)
            window.open(url, '_newtab');
        })
    }

    toggleSlideshow(): void {
        if (this.slideshowInProgress) {
            this.stopSlideshow();
        } else {
            this.slideshow = interval(4000).subscribe((): void => this.move());
            this.slideshowInProgress = true;
        }
    }

    stopSlideshow(): void {
        this.slideshow?.unsubscribe();
        this.slideshow = undefined;
        this.slideshowInProgress = false;
    }

    duplicateMedia(): void {
        const dialogRef: MatDialogRef<MoveMediaComponent> = this.dialog.open(MoveMediaComponent, {
            data: {
                media: this.media[this.index],
                copyMedia: true
            },
            panelClass: 'fullWidthWindow',
        });
    }

    openFullScreen(): void {
        const dialogRef: MatDialogRef<FullScreenImageComponent> = this.dialog.open(FullScreenImageComponent, {
            data: this.media[this.index],
            panelClass: 'fullScreenImage',
        });
    }

}
